import React from 'react';
import { Mail, MapPin, Building, CreditCard, Smartphone, Heart } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { companyInfo, paymentMethods, navigation } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ UPDATED FOOTER EMAIL
  const footerEmail = "info@agentletouchhomecarellc1.com";

  const scrollToSection = (href) => {
    if (location.pathname !== '/' && href.startsWith('#')) {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          const offset = 20;
          const elementPosition = element.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({
            top: elementPosition - offset,
            behavior: 'smooth',
          });
        }
      }, 100);
      return;
    }

    const element = document.querySelector(href);
    if (element) {
      const offset = 20;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth',
      });
    }
  };

  const handleNavClick = (e, item) => {
    if (item.external) return;

    e.preventDefault();

    if (item.isPage) {
      navigate(item.href);
    } else {
      scrollToSection(item.href);
    }
  };

  const getPaymentIcon = (iconName) => {
    switch (iconName) {
      case 'Building':
        return <Building className="w-4 h-4" />;
      case 'CreditCard':
        return <CreditCard className="w-4 h-4" />;
      case 'Smartphone':
        return <Smartphone className="w-4 h-4" />;
      default:
        return null;
    }
  };

  return (
    <footer className="footer py-12 lg:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Grid - 4 columns on large screens */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10 mb-12">

          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img
                src={companyInfo.logo}
                alt="A Gentle Touch HomeCare - Non Medical Home Care in Ohio"
                className="h-24 w-auto"
              />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              Compassionate, dependable, and personalized non medical in home care and
              companionship for aging adults in Ohio.
            </p>
            <div className="flex items-center gap-2 text-gray-400 text-sm">
              <Heart className="w-4 h-4 flex-shrink-0" style={{ color: '#d9b3d9' }} />
              <span>When a Gentle Touch, Can Mean So Much</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {navigation.map((item) => (
                <li key={item.name}>
                  {item.external ? (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-[#20a0a0] text-sm transition-colors"
                    >
                      {item.name}
                    </a>
                  ) : item.isPage ? (
                    <Link
                      to={item.href}
                      className="text-gray-400 hover:text-[#20a0a0] text-sm transition-colors"
                    >
                      {item.name}
                    </Link>
                  ) : (
                    <a
                      href={item.href}
                      onClick={(e) => handleNavClick(e, item)}
                      className="text-gray-400 hover:text-[#20a0a0] text-sm transition-colors"
                    >
                      {item.name}
                    </a>
                  )}
                </li>
              ))}
              <li>
                <Link
                  to="/privacy-policy"
                  className="text-gray-400 hover:text-[#20a0a0] text-sm transition-colors"
                >
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Office Hours & Contact Combined */}
          <div>
            <h3 className="text-white font-semibold mb-4">Office Hours</h3>
            <ul className="space-y-2 mb-6">
              <li className="text-gray-400 text-sm">
                Mon - Thu: 8:00am - 4:00pm
              </li>
              <li className="text-gray-400 text-sm">
                Friday: 8:00am - 3:00pm
              </li>
            </ul>
            
            <h3 className="text-white font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-2">
              <li>
                <a
                  href={`mailto:${footerEmail}`}
                  className="flex items-start gap-2 text-gray-400 hover:text-[#20a0a0] text-sm transition-colors break-all"
                >
                  <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span className="break-all">{footerEmail}</span>
                </a>
              </li>
              <li className="flex items-start gap-2 text-gray-400 text-sm">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Serving families throughout {companyInfo.address}</span>
              </li>
            </ul>
          </div>

          {/* Payment Methods */}
          <div>
            <h3 className="text-white font-semibold mb-4">Payment Methods</h3>
            <div className="space-y-2">
              {paymentMethods.map((method) => (
                <div key={method.name} className="payment-icon">
                  {getPaymentIcon(method.icon)}
                  <span>{method.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-800">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm text-center sm:text-left">
              © {currentYear} A Gentle Touch HomeCare. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <span className="text-gray-500 text-xs">Licensed, Bonded &amp; Insured</span>
              <span className="text-gray-600">•</span>
              <span className="text-gray-500 text-xs">Ohio Home Care Provider</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
